import { useAppStore } from '../../stores/appStore';
import type { AlertLevel, FamilyAlert } from '../../lib/api';

const LEVEL_META: Record<
  AlertLevel,
  {
    label: string;
    emoji: string;
    bg: string;
    border: string;
    pulseClass: string;
    text: string;
  }
> = {
  L1: {
    label: 'L1 · Hey look at me',
    emoji: '👋',
    bg: 'bg-yellow-400',
    border: 'border-yellow-200',
    pulseClass: 'animate-pulse',
    text: 'text-yellow-950',
  },
  L2: {
    label: 'L2 · Important',
    emoji: '⚠️',
    bg: 'bg-orange-500',
    border: 'border-orange-300',
    pulseClass: 'animate-pulse',
    text: 'text-white',
  },
  L3: {
    label: 'L3 · Urgent',
    emoji: '🚨',
    bg: 'alert-l3-flash',
    border: 'border-red-300',
    pulseClass: 'animate-bounce',
    text: 'text-white',
  },
  L911: {
    label: 'L911 · RESPOND NOW',
    emoji: '🆘',
    bg: 'alert-l911-flash',
    border: 'border-red-100',
    pulseClass: 'animate-bounce',
    text: 'text-white',
  },
};

/**
 * Full-screen family alert. Highest-severity active alert is shown first.
 * Tap ✕ to dismiss for everyone (realtime).
 */
export function AlertOverlay() {
  const alerts = useAppStore((s) => s.activeAlerts);
  const dismissAlert = useAppStore((s) => s.dismissAlert);
  const user = useAppStore((s) => s.user);

  if (!alerts.length) return null;

  // Severity order already from API; pick first active
  const alert = alerts[0];
  const meta = LEVEL_META[alert.level] || LEVEL_META.L1;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-3 sm:p-6"
      role="alertdialog"
      aria-modal="true"
      aria-label={meta.label}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className={`relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl border-4 shadow-2xl ${meta.bg} ${meta.border} ${meta.text}`}
      >
        {/* Flashing emoji corner */}
        <div
          className={`absolute left-3 top-3 z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-black/25 text-4xl shadow-lg sm:h-16 sm:w-16 sm:text-5xl ${meta.pulseClass}`}
          aria-hidden
        >
          <span className="emoji-font leading-none">{meta.emoji}</span>
        </div>

        {/* Close */}
        <button
          type="button"
          onClick={() => {
            if (!user) return;
            void dismissAlert(alert.id);
          }}
          className="absolute right-3 top-3 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-black/40 text-2xl font-bold text-white shadow-lg hover:bg-black/60 active:scale-95 sm:h-14 sm:w-14 sm:text-3xl"
          title="Dismiss alert"
          aria-label="Close alert"
        >
          ×
        </button>

        <div className="flex flex-1 flex-col justify-center gap-4 overflow-y-auto px-6 pb-8 pt-20 sm:px-10 sm:pt-24">
          <div className="text-center text-sm font-black uppercase tracking-[0.2em] opacity-90 sm:text-base">
            <span className="emoji-font mr-2">{meta.emoji}</span>
            {meta.label}
          </div>
          {alert.title && (
            <h2 className="text-center text-2xl font-black leading-tight sm:text-4xl">
              {alert.title}
            </h2>
          )}
          <p className="whitespace-pre-wrap text-center text-xl font-semibold leading-snug sm:text-3xl">
            {alert.body}
          </p>
          <p className="mt-2 text-center text-sm font-medium opacity-80">
            From {alert.author_name} ·{' '}
            {new Date(alert.created_at).toLocaleString()}
          </p>
          <p className="text-center text-xs opacity-70">
            Tap × to dismiss on all devices
          </p>
        </div>
      </div>

      {/* Queue indicator if more alerts waiting */}
      {alerts.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white">
          +{alerts.length - 1} more alert{alerts.length > 2 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

export { LEVEL_META };
export type { FamilyAlert };
